import i18n from '@/i18n'

// Backend `detail` strings are English API contract, matched exactly; the `known_errors` check
// requires every key to exist as a backend literal.
const KNOWN_ERRORS: Record<string, string> = {
  'Invalid cedula or password': 'errors.invalidCredentials',
  espera_por_fallos: 'errors.esperaPorFallos',
  'Google login is not configured': 'errors.googleNotConfigured',
  'Invalid Google credential': 'errors.googleInvalidCredential',
  'Google account email is not verified': 'errors.googleEmailNotVerified',
  'This Google account is not registered': 'errors.googleNotRegistered',
  'Not a member of this business': 'errors.notAMember',
  'you have no employee record in this business': 'errors.noSelfRecord',
  'A business with this NIT already exists': 'errors.duplicateNit',
  // Dos 409 por superficie pública: ver `CEDULA_HAS_ACCOUNT_LINK`/`_EMPRESA` en routers/registro.py.
  'This cédula already has an unclaimed Kairos account; this employee link cannot activate it — use "Invitar a la plataforma" from the employee profile instead':
    'errors.cedulaHasAccountLink',
  'This cédula already has a Kairos account; log in instead': 'errors.cedulaHasAccountEmpresa',
  'legal_rep_cedula must match the authenticated user': 'errors.repCedulaMismatch',
  'An employee for this person already exists in this business': 'errors.duplicateEmployeeCedula',
  'An employee for this person already exists in this business as an external advisor with access':
    'errors.duplicateEmployeeCedulaExterno',
  email_en_uso: 'errors.emailInUse',
  email_en_uso_empleado_nuevo: 'errors.emailInUseEmployeeSeed',
  cuenta_ya_reclamada: 'errors.cuentaYaReclamada',
  identidad_compartida: 'errors.identidadCompartida',
  'Invalid or expired token': 'errors.invalidOrExpiredToken',
  'legal_representative_id must be an employee of this business': 'errors.legalRepNotInBusiness',
  'the execution date cannot be in the future': 'errors.fechaEjecucionFutura',
  "the execution date cannot be before the plan's year": 'errors.fechaEjecucionAntesDelPlan',
  'Requires a system user': 'errors.requiresSystemUser',
  'This person is already an administrator': 'admins.errors.alreadyAdmin',
  'the email is already confirmed': 'access.resendConfirmation.errors.yaConfirmado',
  'the account has not been claimed yet': 'access.resendConfirmation.errors.sinReclamar',
  'no email on file to confirm': 'access.resendConfirmation.errors.sinCorreo',
  'too many confirmation emails were sent to this person; try again in a few minutes':
    'access.resendConfirmation.errors.demasiados',
  'too many confirmation emails were sent to this person today; try again tomorrow':
    'access.resendConfirmation.errors.demasiadosHoy',
  'too many invitations were sent to this person today; try again tomorrow': 'access.errors.demasiadasInvitaciones',
  'You cannot deactivate yourself': 'admins.errors.selfDeactivate',
  'Insufficient permissions': 'errors.insufficientPermissions',
  'only an owner can modify the owner': 'errors.onlyOwnerEditsOwner',
  "only an owner can change an employee's role": 'errors.onlyOwnerChangesRole',
  'only an owner can change the status of their own membership': 'errors.onlyOwnerChangesOwnStatus',
  // KAI-438. El 409 que pide confirmar una cédula con cuenta no va acá: abre una confirmación
  // (`hooks/platform/use-access.ts::CONFIRMAR_IDENTIDAD`).
  "Inci has not verified this person's identity": 'verificacionIdentidad.errors.sinVerificar',
  'this identity verification was already resolved': 'verificacionIdentidad.errors.yaResuelta',
  'only a platform administrator can correct a cédula': 'errors.cedulaSoloAdmin',
  'another person already has this cédula': 'errors.cedulaEnUso',
  // KAI-230. El 409 del cupo no va acá: abre una confirmación (`use-usuarios.ts::MEMBRESIA_SOBRE_CUPO`).
  'this person already has a membership in this business': 'usuarios.errors.membresiaYaExiste',
  "this is the business's last active owner": 'usuarios.errors.ultimoDueno',
  'the platform membership is managed in the administrators console': 'usuarios.errors.membresiaDeSistema',
  "this standard's support is not the record of events": 'errors.sinEventosNoAdmitido',
  'this standard already carries a live sin-eventos declaration for the year': 'errors.sinEventosYaDeclarado',
  'Kairos already knows of events in this year, so sin-eventos cannot be declared': 'errors.sinEventosConEventos',
  'a real membership is required to declare sin eventos': 'errors.sinEventosSinMembresia',
  'ciiu_code is not in the CIIU catalog': 'errors.ciiuNotInCatalog',
  "risk_classification_id is not a valid option for the business's CIIU code": 'errors.riskNotValidForCiiu',
  'business profile incomplete': 'errors.businessProfileIncomplete',
  // PQ-2a (E10-H01)
  "matching needs the company's economic activity and risk class": 'perfilCargo.errors.sinActividad',
  'emparejamiento ambiguo: two typical profiles match this position': 'perfilCargo.errors.emparejamientoAmbiguo',
  'no typical profile matched this position': 'perfilCargo.errors.sinPerfilTipico',
  'changing the typical profile needs a catalog key': 'perfilCargo.errors.catalogoKeyRequerida',
  'unknown typical profile key': 'perfilCargo.errors.catalogoDesconocido',
  // C2 (E10 §9): dos llegan estructurados porque la frase necesita la versión vigente o el número de citas.
  'this catalog key already exists': 'errors.catalogoClaveDuplicada',
  'the catalog row changed since you opened it': 'errors.catalogoDesactualizado',
  'this catalog row is cited by a confirmed profile or requirement': 'errors.catalogoConReferencias',
  'this catalog row is already archived': 'errors.catalogoYaArchivada',
  'this catalog row is not archived': 'errors.catalogoNoArchivada',
  'Catalog row not found': 'errors.catalogoNoEncontrado',
  'unknown condition code for the catalog': 'errors.catalogoCondicionDesconocida',
  'unknown requirement type for the catalog': 'errors.catalogoTipoDesconocido',
  'unknown requirement destination for the catalog': 'errors.catalogoDestinoDesconocido',
  'unknown term family for the catalog': 'errors.catalogoFamiliaDesconocida',
  'a term group needs at least two terms': 'errors.terminoGrupoCorto',
  'there is no norm to sign on this catalog row': 'errors.firmaSinNorma',
  'there is no norm article to sign on this catalog row': 'errors.firmaSinArticulo',
  'resolve the norm conflict on this catalog row before signing it': 'errors.firmaConRelacion',
  'some confirmed requirements do not say which catalog row they cite': 'errors.catalogoCitasSinProcedencia',
  'view-as is read-only': 'errors.verComoSoloLectura',
  'the proposal changed since it was shown': 'perfilCargo.errors.propuestaCambio',
  'position has a confirmed profile and cannot be deleted': 'perfilCargo.errors.cargoConPerfil',
  'both positions carry a perfil de cargo: keep one and remove the other first':
    'perfilCargo.errors.perfilesChocanAlUnir',
  // PQ-2b (E10-H02): los cuatro primeros llegan estructurados y sus extras interpolan la frase.
  'the draft changed since you opened it': 'perfilCargo.errors.borradorDesactualizado',
  'a prevention measure must hang from a hazard of this draft': 'perfilCargo.errors.medidaSinPeligro',
  'the scope changed since you reviewed it': 'perfilCargo.errors.alcanceCambio',
  // «Venció» y «cambió» son rechazos distintos: colapsarlos afirmaría un cambio que nadie hizo.
  'the scope preview expired': 'perfilCargo.errors.alcanceVencido',
  'profile is incomplete: confirm functions, at least one requirement and hazards with exposure':
    'perfilCargo.errors.perfilIncompleto',
  'this profile was just confirmed by another signer': 'perfilCargo.errors.perfilRecienConfirmado',
  'there is no draft to confirm for this position': 'perfilCargo.errors.sinBorrador',
  'version not found for this position': 'perfilCargo.errors.versionNoEncontrada',
  'the boss must be an employee of this business': 'perfilCargo.errors.jefeDesconocido',
  'the exigibility date cannot be in the past': 'perfilCargo.errors.exigibleHaciaAtras',
  'the profile is already confirmed: change it through the profile-change flow':
    'perfilCargo.errors.perfilYaConfirmado',
  'the perfil is already sealed': 'perfilSociodemografico.errors.yaSellado',
  // PQ-14 (E1-H02). El 404 de la fila lo cubre `ipevr.errors.filaNotFound`: es el mismo literal.
  'this row has no notice to ignore': 'matrizCargos.errors.sinAviso',
  'this row is not silenced': 'matrizCargos.errors.noSilenciado',
  // PQ-15 (E1-H10, E10-H18)
  'this review was already closed': 'revision.errors.yaCerrada',
  'a "no aplica" decision needs a written reason': 'perfilAprende.errors.motivoRequerido',
  'this suggestion was already decided': 'perfilAprende.errors.yaDecidida',
  'the profile must be confirmed before it can learn': 'perfilAprende.errors.sinPerfilConfirmado',
  // El de «incompleto» llega estructurado y nombra el criterio que falta.
  'this suggestion no longer holds: the evidence changed': 'perfilAprende.errors.sinEvidencia',
  'removing this line would leave the profile incomplete': 'perfilAprende.errors.quitarDejaIncompleto',
  'changing the profile needs a written reason': 'perfilAprende.errors.motivoDeCambioRequerido',
  // PQ-2c (E10-H03)
  'Condition not found': 'condicionCargo.errors.condicionNoEncontrada',
  'a free-text condition needs its text': 'condicionCargo.errors.textoLibreRequerido',
  'this scope is not trivial: review it before confirming': 'condicionCargo.errors.alcancePorRevisar',
  'this condition already has a requirement in the catalog': 'condicionCargo.errors.condicionConRequisito',
  'only a free-text condition carries its own text': 'condicionCargo.errors.textoLibreSoloLibre',
  'this position already has the maximum of free-text conditions': 'condicionCargo.errors.demasiadosLibres',
  'only a confirmed condition can be sent to Inci': 'condicionCargo.errors.condicionSinConfirmar',
  'a condition cannot be confirmed and discarded in the same batch': 'condicionCargo.errors.confirmarYDescartar',
  // PQ-3 (E10-H05/H06)
  'Requirement not found': 'requisitoCargo.errors.requisitoNoEncontrado',
  'removing a requirement needs a written reason': 'requisitoCargo.errors.quitarSinMotivo',
  'tightening a requirement needs a written reason': 'requisitoCargo.errors.apretarSinMotivo',
  'this position has no profile to hang the requirement on': 'requisitoCargo.errors.sinPerfil',
  'this requirement was already removed': 'requisitoCargo.errors.yaQuitado',
  'this requirement was already confirmed': 'requisitoCargo.errors.yaConfirmado',
  'this position already requires that': 'requisitoCargo.errors.duplicado',
  'unexplained red row in batch confirmation': 'requisitoCargo.errors.rojoSinExplicar',
  // PQ-4 (E10-H07/H11)
  'marking a requirement as not applicable needs a written reason': 'cumplimiento.errors.descartarSinMotivo',
  'this requirement is not marked as not applicable': 'cumplimiento.errors.noEstaDescartado',
  'there is no loaded support to confirm on this row': 'cumplimiento.errors.sinSoporte',
  'this requirement has no expected-support slot to file the request under': 'cumplimiento.errors.sinCasilla',
  'the deadline to provide a support cannot be in the past': 'cumplimiento.errors.plazoHaciaAtras',
  'this position has no confirmed profile yet': 'cumplimiento.errors.perfilSinConfirmar',
  'this business has no open assessment to file the request under': 'cumplimiento.errors.sinAnioAbierto',
  'this row already has a live request or a confirmed support': 'cumplimiento.errors.yaPedido',
  // PQ-12 (E10-H14/H13)
  'Restriction clash not found': 'choque.errors.noEncontrado',
  'this restriction clash was already resolved': 'choque.errors.yaResuelto',
  'resolving a restriction clash needs a written reason': 'choque.errors.sinMotivo',
  '«la persona ya no trabaja acá» is written by the retirement, not chosen': 'choque.errors.salidaNoElegible',
  'only the responsable with a real membership can resolve a restriction clash': 'choque.errors.soloCustodio',
  'Contributed support not found': 'miCumplimiento.errors.aportadoNoEncontrado',
  'this support has already been reviewed and can no longer be withdrawn': 'miCumplimiento.errors.aportadoYaRevisado',
  'this person no longer works here, so nothing is asked of them': 'miCumplimiento.errors.retirado',
  // PQ-13 (E1-H07/H08/H11)
  'lifting a restriction needs the passage of the concept that says so, or an explicit administrative note':
    'restriccion.errors.levantarSinSustento',
  'that restriction was already decided; a new concept is what replaces it': 'restriccion.errors.yaDecidida',
  'Restriction not found': 'restriccion.errors.noEncontrada',
  'saying a restriction needs no action requires a written reason': 'restriccion.errors.sinAccionSinMotivo',
  'an action from a restriction needs a responsable and a deadline': 'restriccion.errors.accionSinResponsable',
  'an action can only be committed on a live restriction': 'restriccion.errors.accionNoVigente',
  'someone already wrote why this restriction needs no action': 'restriccion.errors.accionYaDescartada',
  'discarding an occupation-change suggestion needs a written reason': 'cambioOcupacion.errors.descartarSinMotivo',
  'that occupation-change suggestion was already decided': 'cambioOcupacion.errors.yaDecidida',
  'the date someone changed position cannot be in the future': 'cambioOcupacion.errors.fechaFutura',
  'Occupation change suggestion not found': 'cambioOcupacion.errors.noEncontrada',
  // PQ-12 (E10-H08)
  'this position already has an open draft': 'perfilCambio.errors.yaHayBorrador',
  'there is no confirmed version to change yet': 'perfilCambio.errors.sinVersionConfirmada',
  // PQ-5 (E10-H10/H15)
  'profile must be confirmed before generating its document': 'carpetaVisita.errors.perfilSinConfirmar',
  'no position in this business has a confirmed profile yet': 'carpetaVisita.errors.ningunPerfilConfirmado',
  'this folder scope needs the id of what it covers': 'carpetaVisita.errors.alcanceSinReferencia',
  'only the responsable can generate a person-scoped folder with restriction detail':
    'carpetaVisita.errors.carpetaPersonaSoloResponsable',
  'only the responsable can download a folder that carries restriction detail':
    'carpetaVisita.errors.carpetaDetalleSoloResponsable',
  'the folder is still being built': 'carpetaVisita.errors.carpetaEnCurso',
  'Folder not found': 'carpetaVisita.errors.noEncontrada',
  'position has live requirements and cannot be deleted': 'perfilCargo.errors.cargoConRequisitos',
  'both positions carry live requirements: remove one set first': 'perfilCargo.errors.requisitosChocanAlUnir',
  // PQ-6 (E1-H01/H03)
  'IPS not found': 'ips.errors.noEncontrada',
  'this business already has an IPS registered with that name': 'ips.errors.nombreDuplicado',
  'the institutional email is not a valid address': 'ips.errors.correoInvalido',
  'the licence support does not belong to this business': 'ips.errors.soporteNoEncontrado',
  'the licence support must be a PDF or an image': 'ips.errors.soporteNoLeible',
  'Request not found': 'solicitudIps.errors.noEncontrada',
  "this person is not on this business's payroll": 'solicitudIps.errors.personaNoEncontrada',
  'this person has no position registered: the request needs one': 'solicitudIps.errors.sinCargo',
  'that position is not one this person holds': 'solicitudIps.errors.cargoAjeno',
  'this person already has a request generated today and not yet sent': 'solicitudIps.errors.yaGenerada',
  'this batch is capped at 200 people: generate the rest in a second batch': 'solicitudIps.errors.loteLleno',
  'this person holds more than one position: say which one the request covers': 'solicitudIps.errors.dosCargos',
  'the position profile is incomplete: it cannot produce a request': 'solicitudIps.errors.perfilIncompletoSolicitud',
  'two exam slots are due the same day: choose the type': 'solicitudIps.errors.tipoAmbiguo',
  'Kairos does not choose this exam type: confirm it and write why': 'solicitudIps.errors.tipoRequerido',
  'choosing the exam type by hand needs a written reason': 'solicitudIps.errors.motivoRequerido',
  'the request is missing a mandatory fact: fix it at its source': 'solicitudIps.errors.datoFaltante',
  'removing an emphasis or a test needs a written reason': 'solicitudIps.errors.quitarSinMotivo',
  'that emphasis is not among the ones this request proposes': 'solicitudIps.errors.enfasisDesconocido',
  'that condition does not belong to this position': 'solicitudIps.errors.condicionDesconocida',
  'that hazard row does not belong to this business': 'solicitudIps.errors.filaDesconocida',
  'this request has no generated document yet': 'solicitudIps.errors.sinDocumento',
  // PQ-7a (E1-H04), PQ-7d (E1-H05)
  'Quarantined document not found': 'cuarentena.errors.documentoNoEncontrado',
  'that document is no longer in quarantine': 'cuarentena.errors.yaNoRetenido',
  'no IPS with an institutional email is linked to this person': 'cuarentena.errors.sinIpsParaPedir',
  'releasing a quarantined document needs an open assessment to file it into': 'cuarentena.errors.sinAutoevaluacion',
  'this request has no IPS chosen yet: choose one before sending': 'correoIps.errors.sinIpsElegida',
  'the person changed position after this request was generated': 'correoIps.errors.cargoCambiadoTrasGenerar',
  'the risk class changed after this request was generated: regenerate it':
    'correoIps.errors.claseRiesgoCambiadaTrasGenerar',
  'the frozen profile is still the current one: there is nothing to justify': 'correoIps.errors.motivoSinCambio',
  'this person is no longer on the payroll: a request for someone who left is not sent':
    'correoIps.errors.trabajadorRetirado',
  'this request was already sent: confirm it is a resend or send it to another IPS': 'correoIps.errors.yaEnviada',
  'the email could not be sent: the request was not marked as sent': 'correoIps.errors.correoNoSalio',
  'the company name has a line break: fix it before sending to the IPS': 'correoIps.errors.nombreConSalto',
  'that proof of delivery does not belong to this business': 'correoIps.errors.constanciaNoEncontrada',
  'that file is not archived under standard 3.1.3: the proof of delivery belongs there':
    'correoIps.errors.constanciaDeOtroEstandar',
  'the declared send date cannot be in the future': 'correoIps.errors.fechaDeEnvioFutura',
  'that medium says the email left Kairos: pick how it was actually sent': 'correoIps.errors.medioNoEsPorFuera',
  'this request was already loaded as sent outside Kairos: Kairos does not send it again':
    'correoIps.errors.cargadaPorFuera',
  // PQ-7b (E1-H04): todos bajo `cargaIps.*` porque la pantalla pública dice qué hacer con el enlace.
  'no upload link matches that address': 'cargaIps.errors.enlaceNoEncontrado',
  'the upload link for this request has expired': 'cargaIps.errors.enlaceCargaVencido',
  'this upload link has already been used': 'cargaIps.errors.enlaceCargaUsado',
  'this upload link was revoked': 'cargaIps.errors.enlaceCargaRevocado',
  'this upload session expired: open the link again': 'cargaIps.errors.sesionVencida',
  'this link only accepts one PDF, JPEG or PNG file': 'cargaIps.errors.tipoNoAceptado',
  'the uploaded file is not the type it claims to be': 'cargaIps.errors.archivoNoCorresponde',
  'that object does not belong to this upload link': 'cargaIps.errors.objetoAjeno',
  'the window to replace that file is closed': 'cargaIps.errors.ventanaCerrada',
  'there is nothing to replace on this link yet': 'cargaIps.errors.sinCarga',
  'this request has not been sent yet: there is no upload link to renew': 'cargaIps.errors.sinEnviarParaRenovar',
  'the IPS already uploaded through this link: renewing it would change nothing': 'cargaIps.errors.enlaceYaUsado',
  'too many files did not match this request: ask the company for a new link': 'cargaIps.errors.demasiadosRetenidos',
  'the concept already arrived by another path: this link stays closed': 'cargaIps.errors.enlaceCerradoPorConcepto',
  'the concept for this request already arrived: Kairos does not send it again': 'cargaIps.errors.conceptoYaRecibido',
  'position has decided job conditions and cannot be deleted': 'condicionCargo.errors.cargoConCondiciones',
  'both positions carry decided job conditions: decide one of them first':
    'condicionCargo.errors.condicionesChocanAlUnir',
  // PQ-2d (E10-H04)
  'unknown question key': 'preguntaCargo.errors.preguntaDesconocida',
  'this question belongs to a position: position_id is required': 'preguntaCargo.errors.faltaCargo',
  'this is a company-wide question: it takes no position_id': 'preguntaCargo.errors.sobraCargo',
  '"no aplica" needs a written reason': 'preguntaCargo.errors.noAplicaSinMotivo',
  'answering needs the answer itself': 'preguntaCargo.errors.respuestaVacia',
  "the answer must be one of this question's declared options": 'preguntaCargo.errors.opcionDesconocida',
  'this answer does not contradict the risk matrix': 'preguntaCargo.errors.sinContradiccion',
  'the contradiction was already decided by someone else': 'preguntaCargo.errors.decisionCambio',
  // El de borrar llega estructurado: el mensaje cuenta las respuestas y decisiones que se perderían.
  'position has answered questions and cannot be deleted': 'preguntaCargo.errors.cargoConRespuestas',
  'both positions answered the same question: keep one answer and remove the other first':
    'preguntaCargo.errors.preguntasChocanAlUnir',
  // E1-H06 (Res. 1843/2025)
  '"no apto" cannot be proposed, only recorded from a certificate that states it':
    'examenes.normaAnterior.errors.noAptoNoSePropone',
  'a recorded "no apto" verdict cannot be rewritten, only decided on':
    'examenes.normaAnterior.errors.noAptoNoSeReescribe',
  'a written reason is required to move the norma anterior label': 'examenes.normaAnterior.errors.sinMotivo',
  'a written reason needs the decision it belongs to': 'examenes.normaAnterior.errors.sinDecision',
  'the norma anterior decision only applies to a recorded "no apto"':
    'examenes.normaAnterior.errors.decisionSoloNoApto',
  'only the responsable del SG-SST decides on a health concept': 'examenes.normaAnterior.errors.soloCustodio',
  'a correction sends tipo, concepto_aptitud and fecha together': 'examenes.normaAnterior.errors.correccionIncompleta',
  'the examen date cannot be in the future': 'examenes.normaAnterior.errors.fechaFutura',
  'the decision on this examen changed while this tab was open': 'examenes.normaAnterior.errors.decisionYaCambio',
  // PQ-7c (E1-H05)
  '"apto con restricciones" needs at least one written restriction': 'concepto.errors.aptoSinRestriccion',
  'a temporary restriction needs an end date': 'concepto.errors.restriccionSinFecha',
  'a permanent restriction cannot carry an end date': 'concepto.errors.restriccionPermanenteConFecha',
  'a deferred concept needs its written reason and its follow-up date': 'concepto.errors.enCursoSinSeguimiento',
  'two concepts cannot share one radicado: say which one this replaces': 'concepto.errors.conceptoDuplicado',
  'that examen is already anulado': 'concepto.errors.yaAnulado',
  'annulling an examen needs a written reason': 'concepto.errors.anularSinMotivo',
  'a replacement needs the radicado of the request it replaces a concept on': 'concepto.errors.reemplazoSinSolicitud',
  'that examen is not the live concept of this request': 'concepto.errors.reemplazoAjeno',
  'only "apto con restricciones" carries written restrictions': 'concepto.errors.restriccionesSinVeredicto',
  'either all soporte fields are set, or none of them': 'concepto.errors.soporteIncompleto',
  'a recorded exam date cannot be in the future without confirmation': 'concepto.errors.fechaFuturaSinConfirmar',
  // PQ-8 (E1-H09)
  'a pre-filled date can only be shortened, never extended': 'valoracion.errors.fechaAlargada',
  'shortening a pre-filled date needs a written reason': 'valoracion.errors.acortamientoSinMotivo',
  "the verdict has to come from this norm's own catalog": 'valoracion.errors.veredictoAjeno',
  'this special assessment has no verdict catalog loaded yet': 'valoracion.errors.sinNormaCargada',
  'the expiry date cannot be earlier than the assessment date': 'valoracion.errors.vencimientoAnterior',
  'this special assessment is already recorded': 'valoracion.errors.duplicada',
  'annulling a special assessment needs a written reason': 'valoracion.errors.anularSinMotivo',
  'this special assessment is already annulled': 'valoracion.errors.yaAnulada',
  // Los dos 409 son distintos a propósito: el del catálogo manda a Novedades (`isNormaEnCatalogo`).
  'this norm is already in the Kairos catalog; incorporate it from Novedades': 'matrizLegal.propia.errors.enCatalogo',
  'this norm is already declared by this business': 'matrizLegal.propia.errors.yaDeclarada',
  'numero, titulo and nota_aplicabilidad are required': 'matrizLegal.propia.errors.camposRequeridos',
  'anio is outside the accepted range': 'matrizLegal.propia.errors.anioFuera',
  'fuente_url must be an http:// or https:// address': 'matrizLegal.propia.errors.fuenteInvalida',
  'Investigación not found': 'errors.investigacionNotFound',
  'only images, PDF, and modern Excel/Word files are accepted as evidence': 'errors.evidenceBadFormat',
  'the object does not belong to this business and year': 'errors.evidenceForeignObject',
  'upload the file again: this object was not uploaded by you or is already registered': 'errors.evidenceNotIssued',
  'the file was never uploaded to storage': 'errors.evidenceNotUploaded',
  'the uploaded file is larger than declared or than the maximum allowed size': 'errors.archivoMasGrandeQueElDeclarado',
  'this finding already has the maximum number of photos': 'entrevista.foto.demasiadas',
  'this control already has the maximum number of photos': 'ipevr.controles.evidenciaDemasiadas',
  'employee_ids must all belong to this business': 'errors.evidenceBadEmployees',
  'the employee has SG-SST evidence and must be deactivated instead': 'errors.evidenceRetention',
  'the employee has SG-SST designations and must be deactivated instead': 'errors.designationRetention',
  'the evidence is the carta of a live designation': 'errors.designationCartaLocked',
  'the designated employee must belong to this business': 'errors.designationEmployeeNotInBusiness',
  'party and rol are required for committee designations': 'errors.designationCommitteeNeedsPartyRol',
  'party and rol only apply to committee designations': 'errors.designationPartyRolOnlyCommittees',
  'carta_evidence_id must be live evidence of this standard and year': 'errors.designationBadCarta',
  'the standard is not fulfilled by designations': 'errors.designationNotScoped',
  "the standard is not required for the business's chapter": 'errors.standardNotInChapter',
  'the autoevaluación is already registered': 'errors.terminarAlready',
  'every applicable standard must be marked before registering the autoevaluación': 'errors.terminarUnmarked',
  'the autoevaluación is already registered; use a correction to change a mark': 'errors.markFrozen',
  'the autoevaluación is already registered; the regime re-derives when the next year opens': 'errors.rederiveFrozen',
  'the autoevaluación is not registered yet; mark the standard directly': 'errors.correctionTooEarly',
  'a note is required to register a correction': 'errors.correctionNeedsNote',
  'a justification is required to mark a standard as no aplica': 'errors.noAplicaNeedsJustification',
  "resolve or delete the standard's plan actions before marking no aplica": 'errors.noAplicaWithActions',
  'only a no-cumple standard can be resolved from the plan': 'errors.resolveNotNoCumple',
  'the standard has no live plan actions': 'errors.resolveNoActions',
  'every plan action for the standard must be cumplida first': 'errors.resolveActionsPending',
  "the actions were completed before the standard's latest no-cumple mark": 'errors.resolveStale',
  'register the autoevaluación before closing the year': 'errors.closeNeedsTerminar',
  'register the autoevaluación before the ARL report': 'errors.arlNeedsTerminar',
  'the ARL report is already registered': 'errors.arlAlready',
  'an Aceptable result has no ARL report obligation': 'errors.arlAceptable',
  'responsible_employee_id must be an employee of this business': 'errors.actionEmployeeForeign',
  'Action not found in the open year': 'errors.actionNotOpen',
  'a soporte can only be linked to a cumplida action': 'errors.soporteNeedsCumplida',
  'soporte_evidence_id must be evidence of this assessment and standard': 'errors.soporteMismatch',
  'the action supports a resolved standard and cannot be deleted': 'errors.actionBacksResolution',
  'only the owner can revert a plan action': 'errors.memberCannotRevert',
  'the employee is responsible for plan de mejoramiento actions and must be deactivated instead':
    'errors.actionResponsibleRetention',
  'the evidence is the soporte of a plan action and cannot be removed': 'errors.evidenceIsSoporte',
  'the request is no longer pending': 'errors.solicitudNoLongerPending',
  'the standard is marked no aplica and cannot be requested': 'errors.solicitudNoAplica',
  'employee_ids must all belong to this business and be active': 'errors.solicitudBadEmployees',
  'a request reaches at most 200 people: send the rest in a second request': 'errors.solicitudPersonasDeMas',
  'a live request for this standard and person already exists': 'errors.solicitudDuplicate',
  'the assessment year is closed': 'errors.solicitudYearClosed',
  'nit must include its verification digit': 'errors.nitMissingDv',
  'nit must be 6 to 10 digits plus a verification digit': 'errors.nitBadFormat',
  'nit verification digit does not match': 'errors.nitWrongDv',
  'cedula must be 6 to 10 digits': 'errors.cedulaBadFormat',
  'pasaporte must be 6 to 20 letters or digits': 'errors.pasaporteBadFormat',
  'documento must be 6 to 20 letters or digits': 'errors.documentoBadFormat',
  'email is not a valid address': 'errors.emailBadFormat',
  'email must be at most 254 characters': 'errors.emailTooLong',
  'name must be at most 120 characters': 'errors.nameTooLong',
  'phone must be 7 or 10 digits': 'errors.phoneBadFormat',
  // Los literales de `backend/app/models/formatos.py` llegan como `detail` de cadena (`core/errores_de_formato.py`).
  'name must not contain digits': 'errors.nombreConDigitos',
  'dane code must be 5 digits': 'errors.daneBadFormat',
  'year must be 4 digits between 1900 and next year': 'errors.anioNormaInvalido',
  'time must be written as HH:MM': 'errors.horaBadFormat',
  'url must start with http:// or https://': 'errors.urlBadFormat',
  'placa must be 3 letters plus 3 digits or 2 digits and a letter': 'errors.placaBadFormat',
  'request body is too large': 'errors.cuerpoDemasiadoGrande',
  'note must be at most 500 characters': 'errors.notaTooLong500',
  'text must be at most 2000 characters': 'errors.textoMedioTooLong',
  'text must be at most 5000 characters': 'errors.textoTooLong',
  'chronology must be at most 8000 characters': 'errors.cronologiaTooLong',
  'note text is required': 'errors.noteRequired',
  'note text must be at most 2000 characters': 'errors.noteTooLong',
  'password must be at least 8 characters': 'errors.passwordTooShort',
  'password must include a special character': 'errors.passwordNoSpecial',
  'the object does not belong to this person': 'licencia.errors.docKeyForeign',
  'Licencia not found or not pending review': 'licencia.errors.noPendiente',
  'motivo is required to reject a licencia': 'licencia.errors.motivoRequired',
  'motivo must be at most 500 characters': 'licencia.errors.motivoTooLong',
  'Unknown profile fact': 'errors.perfilUnknownFact',
  'value must be a boolean': 'errors.perfilValueNotBoolean',
  'value must be a valid municipio code': 'errors.perfilInvalidMunicipio',
  'value must be a list of strings': 'errors.perfilValueNotList',
  'value contains an option outside the catalog': 'errors.perfilValueOutsideCatalog',
  'quimicos must be confirmed before naming specific substances': 'errors.perfilQuimicosNotConfirmed',
  'Unknown hazard tag': 'errors.perfilUnknownHazard',
  'This hazard is already present for the business': 'errors.perfilHazardAlreadyPresent',
  'Hazard tag not present for this business': 'errors.perfilHazardNotPresent',
  'No open assessment': 'errors.perfilNoOpenAssessment',
  'fecha_aplicacion must not be in the future': 'errors.informeExternoFechaFutura',
  'External report not found': 'errors.informeExternoNoEncontrado',
  'The business has no legal representative': 'errors.perfilNoLegalRep',
  'Municipio not found': 'errors.perfilMunicipioNotFound',
  'attestation requires a real membership': 'marcado.errors.godModeBlocked',
  'a note is required to change an existing mark': 'marcado.errors.noteRequired',
  'not authorized to confirm this item under its gate': 'marcado.errors.confirmForbidden',
  'Sugerencia not found': 'marcado.errors.sugerenciaNotFound',
  'ne must be one of 1, 2, 3, 4': 'ipevr.errors.invalidNe',
  'nc must be one of 10, 25, 60, 100': 'ipevr.errors.invalidNc',
  'area_id must be an área of this business': 'ipevr.errors.areaForeign',
  'Fila not found': 'ipevr.errors.filaNotFound',
  'Evidence not found': 'ipevr.errors.evidenceNotFound',
  'only .xlsx files can be imported': 'ipevr.importar.errors.tipo',
  'the file is not a readable .xlsx workbook': 'ipevr.importar.errors.ilegible',
  'the workbook must have the EMPRESA and MATRIZ sheets': 'ipevr.importar.errors.sinHojas',
  'the workbook is not in the KAIROS-IPEVR v1.0 format': 'ipevr.importar.errors.formato',
  'the workbook methodology must be GTC 45': 'ipevr.importar.errors.metodologia',
  'the MATRIZ sheet is missing required column codes': 'ipevr.importar.errors.columnas',
  'the MATRIZ sheet repeats a column code': 'ipevr.importar.errors.columnasRepetidas',
  'the MATRIZ sheet has more than 1000 rows': 'ipevr.importar.errors.demasiadasFilas',
  'the MATRIZ sheet has more than 5000 rows, counting empty ones': 'ipevr.importar.errors.demasiadasFilasVacias',
  'the workbook is too large once uncompressed': 'ipevr.importar.errors.demasiadoGrande',
  'the workbook NIT does not match the business': 'ipevr.importar.errors.nit',
  'Import report not found': 'ipevr.importar.errors.reporteNoEncontrado',
  "marking the matriz vigente requires a credentialed responsable for the business's banda":
    'ipevr.errors.requiresLicencia',
  'the interview is already complete': 'errors.entrevistaAlreadyComplete',
  'the employee is already approved — remove them through the roster instead': 'errors.rejectAlreadyApproved',
  'jornada_turno is not one of the allowed options': 'errors.jornadaTurnoNotInCatalog',
  'nivel_educativo is not one of the allowed options': 'errors.nivelEducativoNotInCatalog',
  'genero is not one of the allowed options': 'errors.generoNotInCatalog',
  // PYTHON-1Z
  'contract_type is not one of the allowed options': 'errors.contractTypeNotInCatalog',
  'arl must not be empty': 'errors.arlEmpty',
  'eps must not be empty': 'errors.epsEmpty',
  'afp must not be empty': 'errors.afpEmpty',
  'fecha_nacimiento must belong to someone at least 18 years old': 'errors.fechaNacimientoMenorDeEdad',
  // Llegan estructurados (`{"motivo": …}`) y se traducen por el motivo; ver `detalleConMotivo`.
  dudas_sin_decidir: 'errors.bateriaDudasSinDecidir',
  sin_validados: 'errors.bateriaSinValidados',
  // `ReporteControl` la ramifica con `motivoDelError` para decir cuántas personas; esto es el respaldo.
  ficha_incompleta: 'errors.bateriaFichaIncompleta',
  sin_reservado: 'errors.bateriaSinReservado',
  // PYTHON-34
  lote_grande: 'errors.bateriaLoteGrande',
  'this batch is not stalled': 'errors.bateriaTandaNoDetenida',
  'a grave/mortal event needs a verified SST-licensed professional on the team':
    'investigacion.errors.licenciaFaltante',
  'the investigating team is missing required members': 'investigacion.errors.equipoIncompleto',
  'this person is already an afectado of this event': 'incidentes.afectados.errors.duplicado',
  'the event date cannot be in the future': 'errors.futureDateNotAllowed',
  'the investigation start date cannot be before the event': 'incidentes.errors.inicioAntesDelEvento',
  'the investigation start date cannot be in the future': 'incidentes.errors.inicioFuturo',
  'the investigation already has a start date': 'incidentes.errors.inicioYaSellado',
  'the investigation start date only applies when the investigation starts': 'incidentes.errors.inicioSoloAlArrancar',
  // Único literal en español del backend: el gate compara exacto, tildes incluidas.
  'incidente cerrado: reábralo (con un motivo) antes de editarlo': 'errors.incidenteCerrado',
  'motivo is required to delete an incidente': 'incidentes.eliminar.errors.motivoRequerido',
  'this action requires a paid plan': 'plans.upgradeRequired',
  // Respaldo: `isUpgradeGateRequired` abre el modal en vez de un aviso.
  'this change crosses the paid segment band; an upgrade is required': 'planFacturacion.upgradeModal.title',
  // PYTHON-27
  'the proposed change does not cross to a more expensive band': 'planFacturacion.upgradeModal.errorNotAnUpgrade',
  'only .xlsx files are accepted': 'admin.bateriaAdhoc.errors.badFileType',
  'the file must be at most 10 MB': 'admin.bateriaAdhoc.errors.fileTooLarge',
  'razon_social is required to create the business': 'admin.bateriaAdhoc.errors.razonSocialRequired',
  'nit is required to create the business': 'admin.bateriaAdhoc.errors.nitRequired',
  'at least one persona is required': 'admin.bateriaAdhoc.errors.noPersonas',
  'every persona must have a forma (A or B) assigned': 'admin.bateriaAdhoc.errors.formaRequired',
  'every persona must have nombres and apellidos': 'admin.bateriaAdhoc.errors.nombresRequired',
  'every persona must have area, cargo and sede assigned': 'admin.bateriaAdhoc.errors.areaCargoSedeRequired',
  'A tenant business with this NIT already exists': 'admin.bateriaAdhoc.errors.nitBelongsToTenant',
  // La guarda genérica `evaluacion_mutable_or_409` de todo mutador; el copy es el de la carrera al eliminar.
  'the evaluación is closed and can no longer be modified': 'admin.bateria.lifecycle.eliminarErrorClosed',
  'an entregada evaluación is a clinical record under Res. 2764/2022 and cannot be deleted':
    'admin.bateria.lifecycle.eliminarErrorEntregada',
  'motivo is required to delete an evaluación': 'admin.bateria.lifecycle.eliminarErrorMotivoRequerido',
  'this person already has a paquete in this evaluación': 'admin.bateria.agregarPersona.errorDuplicada',
  'a motivo is required to retire a paquete that already has data': 'admin.bateria.retirar.errorMotivo',
  // Respaldo: `AgregarPaquetesDialog` lo detecta con `rosterIncompletoDetalle` y abre el formulario inline.
  roster_incompleto_bateria: 'admin.bateria.rosterIncompleto.generico',
  'the page is not pending': 'admin.bateria.cola.ubicar.errors.yaResuelta',
  // PQ-1
  'unknown company figure key': 'errors.cifraDesconocida',
  'invalid value for this company figure': 'errors.cifraValorInvalido',
  'a written technical justification is required above 12 months': 'errors.cifraSinJustificacion',
  'this SVE validity would leave an occupational exam already expired': 'errors.vigenciaSveDejaVencidos',
  'the scope changed since it was shown': 'errors.alcanceSveCambio',
  'the long IPS deadline must be later than the short one': 'errors.plazosIncoherentes',
  'Position not found': 'errors.cargoNoEncontrado',
  // PQ-9 (E10-H16)
  'Project not found': 'obra.errors.proyectoNoEncontrado',
  'Sede not found': 'obra.errors.sedeNoEncontrada',
  'Assignment not found': 'obra.errors.asignacionNoEncontrada',
  'a project with this name already exists': 'obra.errors.nombreDuplicado',
  'the project end date cannot precede its start date': 'obra.errors.fechasInvertidas',
  'a finished project takes no new assignments': 'obra.errors.proyectoTerminado',
  'hazards can only be confirmed from the rows this project reaches': 'obra.errors.peligroFueraDeLaMatriz',
  'changing the sites would strand hazards this project no longer reaches': 'obra.errors.sedesDejanPeligrosHuerfanos',
  'sede is used by a project and cannot be deleted': 'obra.errors.sedeConObra',
  // «Venció» y «cambió» son rechazos distintos, igual que en el perfil del cargo.
  'the hazard scope changed since you reviewed it': 'obra.errors.alcancePeligrosCambio',
  'the hazard scope preview expired': 'obra.errors.alcancePeligrosVencido',
  'this assignment already ended': 'obra.errors.asignacionYaTerminada',
  "no such person in this company's roster": 'obra.errors.personaNoAsignable',
  'this labor path needs the text that was declared': 'obra.errors.laborTextoRequerido',
  'there is no confirmed position profile to inherit this labor from': 'obra.errors.laborSinPerfil',
  'a contractor link needs a proveedor or a company name': 'obra.errors.vinculoSinEmpresa',
  'unknown proveedor for this business': 'obra.errors.proveedorDesconocido',
  // PQ-10 (E10-H12)
  'authorizing over a red row needs a written reason': 'ingreso.errors.autorizarSinMotivo',
  'the decision receipt does not describe what is on screen': 'ingreso.errors.acuseNoCorresponde',
  'this person is not assigned to that project': 'ingreso.errors.proyectoNoAsignado',
  'Entry decision not found': 'ingreso.errors.decisionNoEncontrada',
  'only the SG-SST responsable custodies this reason': 'ingreso.errors.soloLaCustodia',
  'only the health custodian can change the casilla of a health document': 'evidence.soloCustodiaCambiaCasilla',
  'only the health custodian can remove a health document': 'evidence.soloCustodiaQuita',
  'only the health custodian can act on the AI reading of a health document': 'validation.soloCustodia',
  'this AI reading was requested moments ago; try again in a few minutes': 'validation.reciente',
  'Audit export not found': 'sgsst.export.noEncontrado',
  'this audit export has no manifest; generate it again': 'sgsst.export.regenerar',
  // PQ-11 (E10-H09)
  'Contractor profile not found': 'perfilContratista.errors.perfilNoEncontrado',
  'Profile line not found': 'perfilContratista.errors.lineaNoEncontrada',
  'the delivered profile must be confirmed as a list before contrasting': 'perfilContratista.errors.perfilNoConvertido',
  'a hand-written contractor profile needs at least one line': 'perfilContratista.errors.perfilSinLineas',
  'returning a delivered profile needs a written reason': 'perfilContratista.errors.devolverSinMotivo',
  'this person has no live assignment in that project': 'perfilContratista.errors.personaSinAsignacion',
  'only images, PDF, .xlsx and .docx are accepted as a delivered profile': 'perfilContratista.errors.formatoNoAceptado',
  'the object does not belong to this contractor profile': 'perfilContratista.errors.objetoAjeno',
  'a contractor profile cannot be sourced from the org chart': 'perfilContratista.errors.origenNoAplica',
  'this person has a position in the organigrama; their profile comes from their cargo':
    'perfilContratista.errors.tieneCargoPropio',
  'the contractor profile changed since it was read': 'perfilContratista.errors.perfilCambiado',
  'the object does not belong to this feedback report': 'feedback.errors.objetoAjeno',
  'the feedback file is no longer stored': 'feedback.errors.archivoBorrado',
  'this file was already attached to another feedback report': 'feedback.errors.archivoYaAdjunto',
  'too many feedback reports from this person; try again in a bit': 'feedback.errors.topeReportesUsuario',
  'too many feedback reports from this business today; try again later': 'feedback.errors.topeReportesEmpresa',
  'too many session summaries requested by this person; try again in a bit': 'feedback.errors.topeResumenesUsuario',
  'too many session summaries requested by this business today; try again later':
    'feedback.errors.topeResumenesEmpresa',
}

// Reconocer un error vive en `error-detalle.ts`; se re-exporta para importar todo de `@/lib/errors`.
import {detalleConMotivo, marcarErrorComoComunicado} from './error-detalle'

// Agrupada: el diccionario queda entero acá (check `known_errors`) y el check `tope_de_lineas` topa en 800.
export {
  detalleDelError,
  errorYaComunicado,
  esConceptoDuplicado,
  esFechaFuturaSinConfirmar,
  isActionResponsibleRetentionConflict,
  isBusinessProfileIncomplete,
  isDesignationRetentionConflict,
  isEvidenceRetentionConflict,
  isNormaEnCatalogo,
  isUpgradeGateRequired,
  marcarErrorComoComunicado,
  motivoDelError,
  rosterIncompletoDetalle,
} from './error-detalle'

/** Para un literal que llega dentro de una respuesta 200 y no de un error lanzado. */
export function translateDetail(detail: string): string {
  const key = KNOWN_ERRORS[detail]
  return i18n.t(key ?? 'errors.generic')
}

/** La clave, no la frase: la bitácora del feedback no lleva lo que la persona escribió. */
export function claveDeError(err: unknown): string {
  const detail = (err as {response?: {data?: {detail?: unknown}}})?.response?.data?.detail
  const estructurado = detalleConMotivo(detail)
  const literal = estructurado ? estructurado.motivo : typeof detail === 'string' ? detail : null
  return (literal && KNOWN_ERRORS[literal]) || 'errors.generic'
}

export function translateApiError(err: unknown): string {
  if (err == null) return i18n.t('errors.generic')
  // Quien traduce, pinta: marcar antes de decidir la frase cubre también el genérico.
  marcarErrorComoComunicado(err)
  const detail = (err as {response?: {data?: {detail?: unknown}}}).response?.data?.detail
  const estructurado = detalleConMotivo(detail)
  if (estructurado) {
    const key = KNOWN_ERRORS[estructurado.motivo]
    return key ? i18n.t(key, estructurado) : i18n.t('errors.generic')
  }
  // Un 422 crudo de pydantic trae `detail` como arreglo, no como cadena.
  if (typeof detail !== 'string') return i18n.t('errors.generic')
  const key = KNOWN_ERRORS[detail]
  return i18n.t(key ?? 'errors.generic')
}
